import { useTranslation } from "react-i18next";
import { HeroCharacter } from "@/components/brand/HeroCharacter";
import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Check, CreditCard, Loader2, XCircle } from "lucide-react";
import { Layout } from "@/components/hud/Layout";
import { DeleteAccountZone } from "@/components/billing/DeleteAccountZone";
import { errorFrom } from "@/lib/errors";
import { formatCredits } from "@/lib/creditsApi";
import { cancelSubscription, customerPortal, getBillingStatus, getPublicPlans, packCheckout, subscriptionCheckout, type BillingPack, type BillingPlan, type BillingStatus } from "@/lib/billingApi";
import { useAuth } from "@/lib/auth";
import { resetAccessCache } from "@/lib/access";
import { useLocation, useNavigate } from "react-router-dom";

const eur = (cents: number) => `€${(cents / 100).toFixed(2).replace(".", ",")}`;
const Y = "text-[#E9FF5C]";

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center gap-3 font-display text-xs font-semibold uppercase tracking-[0.34em] text-[#7FD4FF]">
    <span className="h-px w-8 bg-gradient-to-r from-[#2E7BFF] to-transparent" /> {children}
  </span>
);
const Tick = ({ children }: { children: React.ReactNode }) => (
  <li className="flex items-start gap-3 text-sm text-foreground/85"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#7FD4FF]" strokeWidth={2.5} /> <span>{children}</span></li>
);

// Subscriptions (mandatory for AI generation) + optional one-time credit packs. Prices are display-only; Stripe is the payment truth.
export default function Billing() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const [st, setSt] = useState<BillingStatus | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const ANON: Omit<BillingStatus, "plans" | "packs"> = { plan_code: null, subscription_status: null, current_period_start: null, current_period_end: null,
    cancel_at_period_end: false, is_active: false, can_generate_ai: false, credits_balance: 0, credits_per_month: null, discount_eligible: false };
  const load = () => (user ? getBillingStatus() : getPublicPlans().then((p) => ({ ...ANON, ...p }))).then(setSt).catch(() => undefined);

  useEffect(() => {
    if (authLoading) return; // wait for auth state: anonymous → public catalog, user → full status
    load();
    if (params.get("checkout") === "success") { resetAccessCache(); toast.success(t("billing.checkoutSuccess")); }
    if (params.get("checkout") === "cancel") toast(t("billing.checkoutCanceled"));
  }, [user?.id, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = async (key: string, fn: () => Promise<{ checkout_url?: string; url?: string } | BillingStatus>) => {
    if (!user) { // anonymous: pricing is viewable, purchasing requires the existing login/registration flow first
      toast.info(t("access.loginRequired"));
      navigate("/login", { state: { from: location.pathname } });
      return;
    }
    setBusy(key);
    try {
      const r: any = await fn();
      if (r?.checkout_url || r?.url) window.location.assign(r.checkout_url || r.url);
      else setSt(r);
    } catch (e: any) {
      const code = e?.response?.data?.detail?.code;
      toast.error(code === "PORTAL_CONFIGURATION_REQUIRED" ? t("billing.portalUnavailable") : errorFrom(e, "errors.generic"));
    } finally {
      setBusy(null);
    }
  };
  const origin = window.location.origin;
  const statusLabel = (s: string | null) => (s ? t(`billing.status.${s}`, { defaultValue: s }) : "—");
  const credits = (n: number) => formatCredits(n, i18n.language);
  const monthly = st?.plans.find((p) => p.code === "STANDARD_MONTHLY");
  const annual = st?.plans.find((p) => p.code === "ANNUAL");
  const savingCents = monthly && annual ? monthly.price_cents * 12 - annual.price_cents : 0; // 23988 - 19999 = 3989
  const savingPct = monthly && annual ? Math.round((savingCents / (monthly.price_cents * 12)) * 100) : 0; // ≈17
  const ordered = st ? [...st.plans].sort((a, b) => (a.code === "ANNUAL" ? -1 : b.code === "ANNUAL" ? 1 : 0)) : [];

  const PlanCard = ({ p }: { p: BillingPlan }) => {
    const isAnnual = p.code === "ANNUAL";
    const isDiscount = p.code === "DISCOUNT_MONTHLY";
    const cta = isAnnual ? t("billing.ctaAnnual") : isDiscount ? t("billing.subscribe") : t("billing.ctaStandard");
    return (
      <article data-testid={`plan-card-${p.code}`}
        className={`relative flex flex-col rounded-3xl p-8 sm:p-10 ${isAnnual
          ? "glass-panel border-[#7FD4FF]/45 shadow-[0_0_0_1px_rgba(233,255,92,0.22),0_0_60px_-20px_rgba(127,212,255,0.35),0_30px_80px_-40px_rgba(212,255,0,0.4)] md:-my-3 md:py-[calc(2.5rem+0.75rem)]"
          : "glass-panel border-[#7FD4FF]/20"}`}>
        {isAnnual && <span aria-hidden className="absolute inset-x-8 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#E9FF5C] to-transparent" />}
        {isAnnual && (
          <span data-testid="plan-badge-best-value" className="absolute right-8 top-7 rounded-full border border-[#E9FF5C]/60 bg-[#E9FF5C]/10 px-3 py-1 font-display text-[11px] font-bold uppercase tracking-[0.2em] text-[#E9FF5C]">
            {t("billing.bestValue")}
          </span>
        )}
        <h3 className="font-display text-xl font-bold uppercase tracking-[0.18em] text-[#7FD4FF] drop-shadow-[0_0_10px_rgba(127,212,255,0.35)]">{t(`billing.plan.${p.code}`)}</h3>
        <p data-testid={isDiscount ? "plan-discount-reserved" : undefined} className={`mt-2 max-w-[260px] text-sm leading-relaxed ${isDiscount ? "text-[#FFC773]" : "text-foreground/65"}`}>
          {isAnnual ? t("billing.annualTagline") : isDiscount ? t("billing.discountReserved") : t("billing.standardTagline")}
        </p>
        <div className="mt-10 flex flex-wrap items-end gap-x-3 gap-y-2">
          <span data-testid={`plan-price-${p.code}`} className="font-display text-6xl font-bold leading-[0.9] tracking-tight text-foreground">{eur(p.price_cents)}</span>
          <div className="pb-1">
            <span className="block whitespace-nowrap text-sm font-semibold uppercase tracking-[0.18em] text-[#7FD4FF]/85">/ {p.months === 12 ? t("billing.year") : t("billing.month")}</span>
          </div>
        </div>
        {isAnnual && (
          <p data-testid="plan-annual-saving" className={`mt-4 text-sm font-semibold ${Y}`}>{t("billing.saveLine", { amount: eur(savingCents), pct: savingPct })}</p>
        )}
        <p className="mt-8 flex items-baseline gap-2">
          <span className="font-display text-3xl font-bold text-[#7FD4FF] drop-shadow-[0_0_10px_rgba(127,212,255,0.35)]">{credits(p.credits_per_month)}</span>
          <span className="text-sm uppercase tracking-[0.16em] text-foreground/80">{t("credits.label")} / {t("billing.month")}</span>
        </p>
        {isAnnual && <p data-testid="plan-annual-note" className="mt-2 text-sm text-foreground/65">{t("billing.annualMicro")}</p>}
        <span aria-hidden className="my-7 block h-px bg-gradient-to-r from-[#2E7BFF]/30 via-[#2E7BFF]/10 to-transparent" />
        <ul className="space-y-3">
          <Tick>{isAnnual ? t("billing.feat12Months") : t("billing.featAccess")}</Tick>
          <Tick>{t("billing.featEveryMonth", { n: credits(p.credits_per_month) })}</Tick>
          {isAnnual && <Tick>{t("billing.featSaving", { amount: eur(savingCents) })}</Tick>}
          <Tick>{t("billing.featExtra")}</Tick>
          <Tick>{isAnnual ? t("billing.featMonthlyGrant") : t("billing.featManage")}</Tick>
        </ul>
        <div className="flex-1" />
        <button type="button" data-testid={`subscribe-${p.code}`} disabled={!!busy} onClick={() => go(p.code, () => subscriptionCheckout(p.code, origin))}
          className={isAnnual
            ? "cta-yellow mt-10 h-[52px] w-full py-3.5 font-display text-base uppercase tracking-[0.14em]"
            : "mt-10 inline-flex h-[52px] w-full items-center justify-center rounded-md border border-[#E9FF5C]/55 py-3.5 font-display text-base font-bold uppercase tracking-[0.14em] text-[#E9FF5C] hover:bg-[#E9FF5C]/10 disabled:opacity-45"}>
          {busy === p.code ? <Loader2 className="h-4 w-4 animate-spin" /> : cta}
        </button>
      </article>
    );
  };

  const PackCard = ({ p }: { p: BillingPack }) => (
    <article data-testid={`pack-card-${p.code}`} className="glass-panel relative flex flex-col overflow-hidden rounded-2xl border-[#7FD4FF]/20 p-6 sm:p-7">
      <span aria-hidden className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-[#39FF88]/60 to-transparent" />
      <p className="font-display text-2xl font-bold tracking-wide text-[#7FD4FF] drop-shadow-[0_0_10px_rgba(127,212,255,0.35)]">+{credits(p.credits)} <span className="text-base uppercase tracking-[0.16em] text-foreground/80">{t("credits.label")}</span></p>
      <div className="mt-4 flex flex-col gap-1">
        <span data-testid={`pack-price-${p.code}`} className="font-display text-3xl font-bold text-foreground">{eur(p.price_cents)}</span>
      </div>
      <button type="button" data-testid={`buy-${p.code}`} disabled={!!busy || !st?.is_active} onClick={() => go(p.code, () => packCheckout(p.code, origin))}
        className="cta-green mt-6 h-[52px] w-full py-3.5 font-display text-sm uppercase tracking-[0.16em]">
        {busy === p.code ? <Loader2 className="h-4 w-4 animate-spin" /> : t("billing.packAdd")}
      </button>
    </article>
  );

  return (
    <Layout backdrop="full">
      <main data-testid="billing-page" className="relative mx-auto max-w-[1160px] px-6 pb-36 pt-20 sm:px-10 sm:pt-24">
        {/* A. HERO */}
        <HeroCharacter variant="premium" className="-right-[26rem] -top-8" />
        <header className="relative mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-3 font-display text-xs font-semibold uppercase tracking-[0.34em] text-[#E9FF5C] drop-shadow-[0_0_12px_rgba(233,255,92,0.45)]"><span className="h-px w-8 bg-gradient-to-r from-[#E9FF5C] to-transparent" />{t("nav.premium")}<span className="h-px w-8 bg-gradient-to-l from-[#E9FF5C] to-transparent" /></span>
          <h1 data-testid="billing-hero-title" className="headline-shadow mt-7 font-display text-5xl font-bold uppercase leading-[0.95] tracking-[0.02em] text-foreground sm:text-6xl lg:text-7xl max-sm:[overflow-wrap:anywhere]">
            <span className="text-neon-strong block">{t("billing.heroTitleA")}</span>
            <span className="block">{t("billing.heroTitleB")}</span>
          </h1>
          <p data-testid="billing-tagline" className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-foreground/85 md:text-lg">{t("billing.heroSub")}</p>
          <ul className="mx-auto mt-9 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-foreground/85">
            {["checkCredits", "checkManage"].map((k) => (
              <li key={k} className="inline-flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[#7FD4FF]" strokeWidth={2.5} /> {t(`billing.${k}`)}</li>
            ))}
          </ul>
        </header>

        {!st ? (
          <p className="mt-16 inline-flex items-center gap-2 text-sm text-foreground/70"><Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}</p>
        ) : (
          <>
            {/* E. CURRENT PLAN (subscribers) */}
            {st.is_active && (
              <section data-testid="billing-active" className="glass-panel relative mx-auto mt-20 max-w-4xl overflow-hidden rounded-3xl border-[#7FD4FF]/25 p-8 sm:p-10">
                <span aria-hidden className="absolute inset-x-10 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#7FD4FF]/80 to-transparent" />
                <p className="font-display text-sm font-bold uppercase tracking-[0.3em] text-[#7FD4FF] drop-shadow-[0_0_10px_rgba(127,212,255,0.35)]">{t("billing.yourPlan")}</p>
                <div className="mt-6 grid gap-10 sm:grid-cols-3">
                  <div>
                    <p data-testid="billing-plan" className="font-display text-4xl font-bold uppercase tracking-wide text-foreground">{t(`billing.plan.${st.plan_code}`)}</p>
                    <p className="mt-2 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.14em] text-[#E9FF5C]"><BadgeCheck className="h-4 w-4" /> {statusLabel(st.subscription_status)}</p>
                    {st.cancel_at_period_end && <p data-testid="billing-renewal-canceled" className="mt-1 text-sm text-foreground/60">{t("billing.renewalCanceled")}</p>}
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9CC8FF]/80">{t("billing.availableCredits")}</p>
                    <p data-testid="billing-credits" className="mt-2 font-display text-4xl font-bold tracking-wide text-[#7FD4FF] drop-shadow-[0_0_10px_rgba(127,212,255,0.35)]">{credits(st.credits_balance)}</p>
                    {st.credits_per_month && <p className="mt-1 text-sm text-foreground/60">{t("billing.creditsPerMonth", { n: credits(st.credits_per_month) })}</p>}
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9CC8FF]/80">{st.cancel_at_period_end ? t("billing.endsOn") : t("billing.renewsOn")}</p>
                    <p data-testid="billing-period-end" className="mt-2 font-display text-2xl font-bold text-foreground">{st.current_period_end ? new Date(st.current_period_end).toLocaleDateString(i18n.language) : "—"}</p>
                  </div>
                </div>
                {st.discount_eligible && st.plan_code !== "DISCOUNT_MONTHLY" && (
                  <p data-testid="billing-discount-eligible-note" className="mt-6 text-sm text-[#FFC773]">{t("billing.discountEligibleNote")}</p>
                )}
                <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button type="button" data-testid="billing-portal-btn" disabled={!!busy} onClick={() => go("portal", () => customerPortal(origin))} className="cta-yellow h-12 px-7 font-display text-sm uppercase tracking-[0.14em]">
                    <CreditCard className="h-4 w-4" /> {t("billing.manage")}
                  </button>
                  {!st.cancel_at_period_end && (
                    <button type="button" data-testid="billing-cancel-btn" disabled={!!busy} onClick={() => go("cancel", cancelSubscription)}
                      className="inline-flex h-12 items-center justify-center gap-2 rounded-md px-4 text-sm font-medium text-foreground/65 hover:text-foreground disabled:opacity-45">
                      <XCircle className="h-4 w-4" /> {t("billing.cancelRenewal")}
                    </button>
                  )}
                </div>
              </section>
            )}

            {/* non-subscriber with residual credits */}
            {!st.is_active && st.credits_balance > 0 && (
              <section data-testid="billing-required-note" className="glass-panel mx-auto mt-16 flex max-w-3xl flex-col items-start gap-6 rounded-3xl p-8 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9CC8FF]/80">{t("billing.availableCredits")}</p>
                  <p data-testid="billing-credits" className="mt-1 font-display text-4xl font-bold text-[#7FD4FF] drop-shadow-[0_0_10px_rgba(127,212,255,0.35)]">{credits(st.credits_balance)}</p>
                  <p className="mt-3 text-sm font-medium text-foreground/90">{t("billing.residualTitle")}</p>
                  <p className="text-sm text-foreground/65">{t("billing.residualBody")}</p>
                </div>
                <a href="#plans" data-testid="billing-reactivate-btn" className="cta-yellow h-12 shrink-0 px-7 font-display text-sm uppercase tracking-[0.14em]">{t("billing.reactivatePremium")}</a>
              </section>
            )}

            {/* B. VALUE SUMMARY */}
            <section data-testid="billing-value-summary" className="mx-auto mt-24 grid max-w-4xl gap-10 border-y border-[#7FD4FF]/20 py-10 sm:grid-cols-3 sm:gap-8">
              {[["modelSubTitle", "modelSubDesc"], ["modelCreditsTitle", "modelCreditsDesc"], ["modelExtraTitle", "modelExtraDesc"]].map(([h, d], i) => (
                <div key={h} className="sm:px-2">
                  <p className="font-display text-sm font-bold uppercase tracking-[0.3em] text-[#7FD4FF] drop-shadow-[0_0_10px_rgba(127,212,255,0.35)]">0{i + 1} · {t(`billing.${h}`)}</p>
                  <p className="mt-3 text-base leading-relaxed text-foreground/90">{t(`billing.${d}`)}</p>
                </div>
              ))}
            </section>

            {/* C. PRICING */}
            {!st.is_active && (
              <section id="plans" data-testid="billing-plans" className={`mx-auto mt-24 grid items-stretch gap-6 md:grid-cols-2 md:gap-7 ${ordered.length > 2 ? "max-w-[1160px] xl:grid-cols-3" : "max-w-4xl"}`}>
                {ordered.map((p) => <PlanCard key={p.code} p={p} />)}
                <p data-testid="billing-trust-line" className="mt-6 text-center text-sm text-foreground/55 md:col-span-2 xl:col-span-3">{t("billing.trustLine")}</p>
                <p data-testid="billing-legal-note" className="text-center text-xs text-foreground/50 md:col-span-2 xl:col-span-3">
                  {t("billing.vatIncluded")} · <Link to="/terms" data-testid="billing-terms-link" className="underline-offset-4 hover:underline">{t("legal.terms")}</Link> · <Link to="/privacy" data-testid="billing-privacy-link" className="underline-offset-4 hover:underline">{t("legal.privacy")}</Link>
                </p>
              </section>
            )}

            {/* D. CREDIT PACKS */}
            <section id="packs" data-testid="billing-packs" className="mx-auto mt-24 max-w-4xl">
                <div className="max-w-xl">
                  <span className="inline-flex items-center gap-3 font-display text-xs font-semibold uppercase tracking-[0.34em] text-[#39FF88]"><span className="h-px w-8 bg-gradient-to-r from-[#39FF88] to-transparent" /> {t("billing.packsEyebrow")}</span>
                  <h2 className="mt-4 font-display text-base font-bold uppercase tracking-[0.18em] text-[#7FD4FF] drop-shadow-[0_0_10px_rgba(127,212,255,0.35)] md:text-lg">{t("billing.packsTitle")}</h2>
                  <p data-testid="billing-packs-only-subscribers" className="mt-3 text-sm leading-relaxed text-foreground/85">{t("billing.packsSub")}</p>
                </div>
                <div className="mt-8 grid gap-5 sm:grid-cols-2">
                  {st.packs.map((p) => <PackCard key={p.code} p={p} />)}
                </div>
                {!st.is_active && <p data-testid="billing-packs-locked" className="mt-6 text-sm text-foreground/70">{t("billing.packsOnlySubscribers")}</p>}
                {st.is_active && <p className="mt-6 text-sm text-foreground/55">{t("billing.trustLine")}</p>}
                {st.is_active && (
                  <p data-testid="billing-legal-note-active" className="mt-2 text-xs text-foreground/50">
                    {t("billing.vatIncluded")} · <Link to="/terms" data-testid="billing-terms-link-active" className="underline-offset-4 hover:underline">{t("legal.terms")}</Link> · <Link to="/privacy" data-testid="billing-privacy-link-active" className="underline-offset-4 hover:underline">{t("legal.privacy")}</Link>
                  </p>
                )}
              </section>
              <DeleteAccountZone />
          </>
        )}
      </main>
    </Layout>
  );
}
